import { commonServerInstance } from '@/utilities/instances'

const url_path_map = {
  US: {
    downloadParcels: '/api/v1/parcels/us/download',
    downloadParcelsUseRedShift: '/api/v3/parcels/us/download-redshift',
    uploadTnoToGetParcels: '/api/v3/parcels/us/direct-tno-download',
    getPartners: '/api/v1/parcels/partners'
  },
  CA: {
    downloadParcels: '/api/v1/parcels/ca/download',
    uploadTnoToGetParcels: '/api/v1/parcels/ca/direct-tno-download',
    getPartners: '/api/v1/ca/rate-card/partner-carrier-config/partners'
  }
}

const COUNTRY = import.meta.env.VITE_APP_COUNTRY as keyof typeof url_path_map

const queueMap = {
  US: 'longjob,mysql-longjob',
  CA: 'longjob,mysql-longjob'
} as const

export interface ReasonCategoryOption {
  key: string
  label: string
}

export const getDownloadReasonCategories = async (): Promise<
  ReasonCategoryOption[]
> => {
  const response = await commonServerInstance.get(
    '/api/v3/parcels/us/download-reason-categories'
  )
  return response?.data?.data ?? []
}

export function downloadParcels(params: any) {
  return commonServerInstance.post(
    `${url_path_map[COUNTRY].downloadParcels}`,
    params
  )
}

export interface RedshiftDownloadParams {
  partner?: number[]
  reason_category: string
  justification: string
  split: number
  columns: string
  [key: string]: unknown
}

export function downloadParcelsUseRedShift(params: RedshiftDownloadParams) {
  return commonServerInstance.post(
    url_path_map.US.downloadParcelsUseRedShift,
    params
  )
}

interface UploadParams {
  file: File
  columns: string
  reason_category?: string
  justification?: string
}

const appendUploadReason = (formData: FormData, params: UploadParams) => {
  if (params.reason_category) {
    formData.append('reason_category', params.reason_category)
  }
  if (params.justification) {
    formData.append('justification', params.justification)
  }
}

export const uploadTnoToGetParcels = async (params: UploadParams) => {
  const formData = new FormData()
  formData.append('file', params.file)
  formData.append('columns', params.columns)
  appendUploadReason(formData, params)
  const response = await commonServerInstance.post(
    `${url_path_map[COUNTRY].uploadTnoToGetParcels}`,
    formData
  )
  return response?.data?.data
}

export const uploadThirdPartyTnoToGetParcels = async (params: UploadParams) => {
  const formData = new FormData()
  formData.append('file', params.file)
  formData.append('columns', params.columns)
  appendUploadReason(formData, params)
  const response = await commonServerInstance.post(
    '/api/v3/parcels/us/usps-tno-download',
    formData
  )
  return response?.data?.data
}

export const uploadPackageIdToGetParcels = async (params: UploadParams) => {
  const formData = new FormData()
  formData.append('file', params.file)
  formData.append('columns', params.columns)
  appendUploadReason(formData, params)
  const response = await commonServerInstance.post(
    '/api/v3/parcels/us/package-id-download',
    formData
  )
  return response?.data?.data
}

export const getOrderDownloadJobs = async (params?: { email?: string }) => {
  const response = await commonServerInstance.get(
    '/api/v1/jobs/parcel-downloads',
    {
      params
    }
  )
  return response?.data?.data
}

export const getOrderDownloadQueueJobs = async (params?: {
  queue?: string
}) => {
  const country = import.meta.env.VITE_APP_COUNTRY as keyof typeof queueMap
  const defaultQueue = queueMap[country] || queueMap.US

  const response = await commonServerInstance.get('/api/v1/queue-status', {
    params: {
      queue: params?.queue || defaultQueue
    }
  })
  return response?.data?.data
}

export const getPartners = async () => {
  const response = await commonServerInstance.get(
    url_path_map[COUNTRY].getPartners
  )
  return COUNTRY === 'US'
    ? (response?.data?.data?.partners ?? [])
    : (response?.data?.data ?? [])
}
